import React from "react";
import * as XLSX from "xlsx";
import PageHeader from "../PageHeader";
import StatCard from "../StatCard";
import StockPanel from "./StockPanel";
import SetupRun from "./SetupRun";
import {
  WAREHOUSE,
  stockRows,
  allLocationNames,
  lowRows,
  availableUnits,
  TONE_SHORT,
} from "./consumableHelpers";

// The consumables screen.
//
// Stock is a row per type measured against its reorder line, at full width, with the
// per-location split on the row itself. Reorder points are fleet-wide, so a type is
// short on its total across every site.
//
// Setting up tracked units is a scanning session, not a view of the stock — it gets
// its own screen (reached from the header, returned from by "Back to stock") rather
// than a tab competing with the list. Stock locations are admin configuration and now
// live in Settings > Consumables, next to the type settings they belong with.
export default function ConsumablesView({ st, setSt, isReadOnly, isAdmin, audit, account }) {
  const [expandedId, setExpandedId] = React.useState(null);
  const [screen, setScreen] = React.useState("stock");

  const types = React.useMemo(() => st.consumables || [], [st.consumables]);
  const trackedTypes = React.useMemo(() => types.filter(t => t.tracked), [types]);

  const rows = React.useMemo(
    () => stockRows(types, st.consumableStock, st.consumableQty),
    [types, st.consumableStock, st.consumableQty]
  );
  const locations = React.useMemo(
    () => allLocationNames(rows, st.consumableLocations),
    [rows, st.consumableLocations]
  );
  const low = React.useMemo(() => lowRows(rows), [rows]);
  const shortRows = React.useMemo(() => low.filter(r => r.tone === TONE_SHORT), [low]);

  const totals = React.useMemo(() => {
    const onHand = rows.reduce((n, r) => n + r.total, 0);
    const warehouse = rows.reduce((n, r) => n + (r.byLocation[WAREHOUSE] || 0), 0);
    return { onHand, warehouse, onSite: onHand - warehouse };
  }, [rows]);

  const availableCount = React.useCallback(
    typeId => availableUnits(typeId, st.consumableStock).length,
    [st.consumableStock]
  );

  // ── Mutations ──────────────────────────────────────────────────────────────

  // A setup run creates one row per scanned code. Units start at the chosen holding
  // location; "the warehouse" is stored as no location, matching how the rest of the
  // app reads an unissued unit.
  const assignRun = ({ type, locationName, codes }) => {
    const now = new Date().toISOString();
    const by = account?.name || "Unknown";
    const atWarehouse = locationName === WAREHOUSE;
    const entries = codes.map(code => ({
      id: String(Date.now()) + "-" + code,
      typeId: type.id,
      typeName: type.name,
      serial: "",
      qrCode: code,
      status: atWarehouse ? "in_stock" : "dispatched",
      locationName: atWarehouse ? null : locationName,
      batchId: null,
      addedAt: now,
      addedBy: by,
      retiredAt: null,
      notes: "",
    }));
    setSt(s => ({ ...s, consumableStock: [...(s.consumableStock || []), ...entries] }));
    audit("CONSUMABLE_STOCK_ADDED", {
      typeName: type.name,
      count: entries.length,
      locationName,
      mode: "setup_run",
    });
  };

  const adjustQty = ({ typeId, locationName, delta, note }) => {
    const type = types.find(t => t.id === typeId);
    setSt(s => {
      const qty = { ...(s.consumableQty || {}) };
      const atLoc = { ...(qty[locationName] || {}) };
      const next = (atLoc[typeId] || 0) + delta;
      if (next <= 0) delete atLoc[typeId];
      else atLoc[typeId] = next;
      if (Object.keys(atLoc).length === 0) delete qty[locationName];
      else qty[locationName] = atLoc;
      return { ...s, consumableQty: qty };
    });
    audit("CONSUMABLE_QTY_ADJUSTED", {
      typeName: type ? type.name : typeId,
      locationName,
      delta,
      note,
    });
  };

  const exportStock = () => {
    const header = ["Type", "Held as", "Total", "Reorder point", "State", ...locations];
    const body = rows.map(r => [
      r.name,
      r.tracked ? "Tracked individually" : "Counted",
      r.total,
      r.reorderPoint || "",
      r.tone === TONE_SHORT ? "Short" : r.tone === "low" ? "Low" : "OK",
      ...locations.map(l => r.byLocation[l] || 0),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    ws["!cols"] = [
      { wch: 18 },
      { wch: 18 },
      { wch: 10 },
      { wch: 14 },
      { wch: 10 },
      ...locations.map(() => ({ wch: 16 })),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Consumables");
    XLSX.writeFile(wb, "consumables-" + new Date().toISOString().slice(0, 10) + ".xlsx");
  };

  // Jump from a shortage pill to the row it names, and open it.
  const focusRow = id => {
    setExpandedId(id);
    const el = document.getElementById("consumable-row-" + id);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  // ── Set-up screen ──────────────────────────────────────────────────────────

  if (screen === "setup") {
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <PageHeader
            title="Set up units"
            sub="Give tracked units their QR codes, a run at a time"
            actions={
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setScreen("stock")}
              >
                Back to stock
              </button>
            }
          />
        </div>
        <div style={{ maxWidth: 680 }}>
          <SetupRun
            types={trackedTypes}
            locations={locations}
            consumableStock={st.consumableStock}
            onAssign={assignRun}
            disabled={isReadOnly}
            onDone={() => setScreen("stock")}
          />
        </div>
      </div>
    );
  }

  // ── Stock screen ───────────────────────────────────────────────────────────

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <PageHeader
          title="Consumables"
          sub="What is held, where it is held, and what needs reordering"
          actions={
            <>
              {rows.length > 0 && (
                <button type="button" onClick={exportStock} className="btn btn-ghost btn-sm">
                  Export
                </button>
              )}
              {!isReadOnly && trackedTypes.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setScreen("setup")}
                >
                  Set up units
                </button>
              )}
            </>
          }
        />
      </div>

      {/* Three tiles: how much, of how many kinds, and how much of it is a problem. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard
          label="Items on hand"
          value={totals.onHand.toLocaleString("en-GB")}
          sub={
            totals.warehouse.toLocaleString("en-GB") +
            " in the warehouse · " +
            totals.onSite.toLocaleString("en-GB") +
            " out on site"
          }
        />
        <StatCard
          label="Types"
          value={String(rows.length)}
          sub={
            trackedTypes.length +
            " tracked individually · " +
            (rows.length - trackedTypes.length) +
            " counted"
          }
        />
        <StatCard
          label="Below reorder"
          value={String(shortRows.length)}
          valueColor={shortRows.length > 0 ? "var(--danger-text)" : undefined}
          sub={
            shortRows.length === 0
              ? low.length === 0
                ? "Everything above its line"
                : low.length + " approaching its line"
              : shortRows.slice(0, 3).map(r => r.name).join(", ")
          }
        />
      </div>

      {/* Only a real shortfall gets a strip of its own — "approaching" is already
          visible as an amber pill on the row, and repeating it here taught the same
          thing three times before the list was reached. */}
      {shortRows.length > 0 && (
        <div
          className="card"
          style={{
            padding: "12px 16px",
            marginBottom: 16,
            borderColor: "var(--danger-border)",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--danger-text)" }}>
            Below reorder point
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {shortRows.map(r => (
              <button
                key={r.id}
                type="button"
                className="pill pill-danger"
                onClick={() => focusRow(r.id)}
                style={{ cursor: "pointer", fontFamily: "inherit" }}
              >
                {r.name} · {r.total} of {r.reorderPoint}
              </button>
            ))}
          </div>
        </div>
      )}

      <StockPanel
        rows={rows}
        expandedId={expandedId}
        onToggle={id => setExpandedId(cur => (cur === id ? null : id))}
        locations={locations}
        quantities={st.consumableQty}
        onAdjust={adjustQty}
        availableCount={availableCount}
        canEdit={!isReadOnly}
      />

      {isAdmin && (
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-subtle)" }}>
          Types, reorder points and stock locations are set in{" "}
          <strong>Settings &rsaquo; Consumables</strong>.
        </div>
      )}
    </div>
  );
}
